import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { createBatchFetcherByKey, fetchApiWithToken } from '~/api/utils'
import { toISOLocal } from '~/utils'

type PathActivityData = {
  path: string
  lastViewedDate: string | null
  lastActivityDate: string
}

const pathViewedActivityBatcher = createBatchFetcherByKey<
  PathActivityData,
  string
>((item) => item.path)({
  fetcher: (paths: string[]) =>
    fetchApiWithToken<PathActivityData[]>({
      url: `/v1/path-viewed/activity?${paths.map((p) => `path[]=${encodeURIComponent(p)}`).join('&')}`,
      method: 'GET'
    })
})

export type PathViewedActivityEvent = {
  path: string
  lastActivityDate: string
}

type ActivityListener = (event: PathViewedActivityEvent) => void

// Callbacks waiting for a real-time event on a path (or on one of its children), keyed by path
const activityListeners = new Map<string, Set<ActivityListener>>()

const notifyActivityListeners = (event: PathViewedActivityEvent): void => {
  for (const path of getPathHierarchy(event.path)) {
    for (const listener of activityListeners.get(path) ?? []) {
      listener(event)
    }
  }
}

type PathViewedActivityOptions = {
  /** State reported while nothing is known yet */
  defaultState?: boolean
  /** Called when a real-time `path-viewed` event lands on the path or on one of its children */
  onNewActivity?: ActivityListener
}

export const usePathViewedActivity = (
  path?: string,
  { defaultState = false, onNewActivity }: PathViewedActivityOptions = {}
) => {
  const onNewActivityRef = useRef(onNewActivity)
  onNewActivityRef.current = onNewActivity
  const listens = !!path && !!onNewActivity

  useEffect(() => {
    if (!path || !listens) return
    const listener: ActivityListener = (event) =>
      onNewActivityRef.current?.(event)
    const listeners = activityListeners.get(path) ?? new Set()
    listeners.add(listener)
    activityListeners.set(path, listeners)
    return () => {
      listeners.delete(listener)
      if (listeners.size === 0) activityListeners.delete(path)
    }
  }, [path, listens])

  const { data, isLoading, ...query } = useQuery<PathActivityData | null>({
    queryKey: ['pathViewedActivity', path],
    queryFn: async () => {
      if (!path) return null
      return await pathViewedActivityBatcher.fetch(path)
    },
    enabled: !!path,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  const hasNewActivity = data
    ? data.lastActivityDate > (data.lastViewedDate ?? '')
    : isLoading
      ? false
      : defaultState

  return {
    data,
    isLoading,
    ...query,
    hasNewActivity
  }
}

type TrackPathViewedPayload = {
  path: string
}

/**
 * Applies a real-time `path-viewed` event: the path's cached activity is updated in place, its
 * parents are refetched, since their state is derived server-side from all their children, and
 * the listeners registered through `usePathViewedActivity` are called.
 */
export const applyPathViewedActivity = (
  queryClient: QueryClient,
  event: PathViewedActivityEvent
): void => {
  const { path, lastActivityDate } = event
  queryClient.setQueryData(
    ['pathViewedActivity', path],
    // Events can land out of order: never move the activity date backwards
    (oldData: PathActivityData | null | undefined): PathActivityData => ({
      path,
      lastViewedDate: oldData?.lastViewedDate ?? null,
      lastActivityDate:
        oldData && oldData.lastActivityDate > lastActivityDate
          ? oldData.lastActivityDate
          : lastActivityDate
    })
  )
  getPathHierarchy(path)
    .slice(1)
    .forEach((parent) => {
      void queryClient.invalidateQueries({
        queryKey: ['pathViewedActivity', parent]
      })
    })
  notifyActivityListeners(event)
}

const getPathHierarchy = (path: string): string[] => {
  const segments = path.split('/').filter(Boolean)
  const paths: string[] = []

  for (let i = segments.length; i > 0; i--) {
    const currentPath = `/${segments.slice(0, i).join('/')}`
    paths.push(currentPath)
  }

  return paths
}

export const useTrackPathViewedMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, TrackPathViewedPayload>({
    mutationFn: ({ path }: TrackPathViewedPayload) =>
      fetchApiWithToken({
        method: 'POST',
        url: '/v1/path-viewed/track',
        data: { path }
      }),
    onSuccess: (_, { path }) => {
      const now = toISOLocal(new Date())
      queryClient.setQueryData(
        ['pathViewedActivity', path],
        (oldData: PathActivityData | null) => {
          if (oldData) {
            return { ...oldData, lastViewedDate: now }
          }
          return oldData
        }
      )
      // A parent's activity is derived server-side from all its children: other siblings may
      // still be unread, so parents are refetched rather than patched.
      getPathHierarchy(path)
        .slice(1)
        .forEach((parent) => {
          void queryClient.invalidateQueries({
            queryKey: ['pathViewedActivity', parent]
          })
        })
    }
  })
}
